import getApiUrl from 'src/config/api';
import { selectFingerprint } from 'src/modules/User';
import ApiError from 'src/utils/error';
import { ACTION_TYPES, MODULE_NAME } from './constants';

export const setSelectedMenuPosition = (item) => async (dispatch) => {
    dispatch({
        type: ACTION_TYPES.SET_SELECTED_MENU_POSITION,
        payload: item
    });
};

export const increaseQuantity = () => async (dispatch) => {
    dispatch({
        type: ACTION_TYPES.INCREASE_QUANTITY
    });
};

export const decreaseQuantity = () => async (dispatch) => {
    dispatch({
        type: ACTION_TYPES.DECREASE_QUANTITY
    });
};

export const submitOrder = (context, paymentContinueUrl) => async (dispatch, getState) => {
    const { sessionId } = context;
    const fingerprint = selectFingerprint(getState());

    dispatch({
        type: ACTION_TYPES.SUBMIT_ORDER_PENDING
    });

    await fetch(`${getApiUrl()}/api/client/clientSessions/${sessionId}/bill/orders`, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            fingerprint,
            paymentContinueUrl
        })
    }).then((response) => {
        if (response.status !== 200) {
            return response.json().then((json) => { throw new ApiError(json); });
        } else {
            return response.json();
        }
    }).then((data) => {
        dispatch({
            type: ACTION_TYPES.SUBMIT_ORDER_SUCCESS,
            payload: data
        });
    }).catch((error) => {
        dispatch({
            type: ACTION_TYPES.SUBMIT_ORDER_FAILED,
            payload: error?.firstMessage
        });
    });
};

export const clearSubmitErrorMessage = () => (dispatch) => {
    dispatch({
        type: ACTION_TYPES.CLEAR_SUBMIT_ORDER_ERROR
    });
};

export const changeOrderPositionQuantity = (positionId, context, quantity) => async (dispatch, getState) => {
    const { sessionId } = context;
    const fingerprint = selectFingerprint(getState());

    if (quantity === 0) {
        await fetch(`${getApiUrl()}/api/client/clientSessions/${sessionId}/cart/positions/${positionId}`, {
            method: 'DELETE',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                fingerprint
            })
        }).then(async (response) => {
            if (response.status === 200) {
                dispatch({
                    type: ACTION_TYPES.REMOVE_ORDER_POSITION,
                    payload: positionId
                });
            } else {
                return response.json().then((json) => { throw new ApiError(json); });
            }
        }).catch((error) => {
            dispatch({
                type: ACTION_TYPES.REMOVE_ORDER_POSITION_FAILED,
                payload: error?.firstMessage
            });
        });
    } else {
        await fetch(`${getApiUrl()}/api/client/clientSessions/${sessionId}/cart/positions/${positionId}`, {
            method: 'PUT',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                amount: quantity,
                fingerprint
            })
        }).then((response) => {
            if (response.status === 200) {
                dispatch({
                    type: ACTION_TYPES.CHANGE_QUANTITY_ORDER_POSITION,
                    payload: {
                        positionId,
                        quantity
                    }
                });
            } else {
                return response.json().then((json) => { throw new ApiError(json); });
            }
        }).catch((error) => {
            dispatch({
                type: ACTION_TYPES.CHANGE_QUANTITY_ORDER_POSITION_FAILED,
                payload: error?.firstMessage
            });
        });
    }
};

export const increaseOrderPositionQuantity = (position, context) => async (dispatch, getState) => {
    await changeOrderPositionQuantity(position.id, context, position.quantity + 1)(dispatch, getState);
};

export const decreaseOrderPositionQuantity = (position, context, shouldRemoveOrder = false) => async (dispatch, getState) => {
    await changeOrderPositionQuantity(position.id, context, position.quantity - 1, shouldRemoveOrder)(dispatch, getState);
};

const validatePositon = (position, errors = []) => {
    const isValid = (position.addonGroups || []).reduce((result, addonGroup) => {
        const selectedAddons = addonGroup.addons.filter((addon) => addon.quantity > 0 || addonGroup.selectedAddon === addon.virtualId);

        if (selectedAddons.length > addonGroup.maxQuantity) {
            errors.push({
                groupId: addonGroup.virtualId,
                error: 'selectedPositionsOverLimit'
            });

            return false;
        }

        if (selectedAddons.length < addonGroup.minQuantity) {
            errors.push({
                groupId: addonGroup.virtualId,
                error: 'selectedPositionsUnderLimit'
            });

            return false;
        }

        const validationResult = selectedAddons.map((addon) => validatePositon(addon, errors));

        if (validationResult.some(({ isValid: isNestedInvalid }) => !isNestedInvalid)) {
            return false;
        }

        if (addonGroup.controlType === 'configurable' || addonGroup.controlType === 'multi') {
            const addonsQuantity = addonGroup.addons.reduce((sum, item) => sum + item.quantity, 0);

            if (addonsQuantity > addonGroup.maxQuantity) {
                errors.push({
                    groupId: addonGroup.virtualId,
                    error: 'selectedPositionsOverLimit'
                });

                return false;
            }

            if (addonsQuantity < addonGroup.minQuantity) {
                errors.push({
                    groupId: addonGroup.virtualId,
                    error: 'selectedPositionsUnderLimit'
                });

                return false;
            }
        }

        return result;
    }, true);

    return { isValid, errors };
};

export const validateOrderPosition = (item) => async (dispatch) => {
    const { isValid, errors } = validatePositon(item);

    if (!isValid) {
        dispatch({
            type: ACTION_TYPES.ORDER_POSITION_VALIDATED,
            payload: errors
        });
    }

    return isValid;
};

const prepareAddons = (position) => (position.addonGroups || []).reduce((result, addonGroup) => {
    const selectedAddons = addonGroup.addons.filter((item) => item.quantity > 0 || addonGroup.selectedAddon === item.virtualId);

    const nestedAddons = selectedAddons.map((item) => ({
        addons: prepareAddons(item),
        addonGroupId: addonGroup.id,
        addonId: item.id,
        amount: item.quantity || 1
    }));

    return [...result, ...nestedAddons];
}, []) || [];

export const addOrderPosition = (item, context) => async (dispatch, getState) => {
    const { sessionId } = context;

    const isValid = await validateOrderPosition(item)(dispatch);

    if (!isValid) {
        return;
    }

    const fingerprint = selectFingerprint(getState());

    await fetch(`${getApiUrl()}/api/client/clientSessions/${sessionId}/cart/positions`, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            menuPositionId: item.id,
            amount: item.quantity,
            fingerprint,
            comment: item.comment,
            addons: prepareAddons(item)
        })
    }).then((response) => {
        if (response.status === 200) {
            dispatch({
                type: ACTION_TYPES.ORDER_POSITION_ADDED,
                payload: item
            });
        } else {
            return response.json().then((json) => { throw new ApiError(json); });
        }
    }).catch((error) => {
        dispatch({
            type: ACTION_TYPES.ADD_ORDER_POSITION_FAILED,
            payload: error?.firstMessage
        });
    });
};

export const clearAddOrderPositionErrorMessage = () => async (dispatch) => {
    dispatch({
        type: ACTION_TYPES.CLEAR_ADD_ORDER_POSITION_ERROR_MESSAGE
    });
};

export const clearSelectedMenuPosition = () => async (dispatch) => {
    dispatch({
        type: ACTION_TYPES.SELECTED_MENU_POSITION_CLEARED
    });
};

export const connectOrdersSocket = ({ sessionId }) => async (dispatch, getState, di) => {
    di[MODULE_NAME].orderWebSocket.connect(
        dispatch,
        getState,
        `api/client/clientSessions/${sessionId}/clientSummaries`
    );
};

export const disconnectOrdersSocket = () => async (dispatch, getState, di) => {
    const { orderWebSocket } = di[MODULE_NAME];

    if (orderWebSocket.socket.connection) {
        orderWebSocket.close();
    }
};

export const connectBillSocket = ({ sessionId }) => async (dispatch, getState, di) => {
    di[MODULE_NAME].billWebSocket.connect(
        dispatch,
        getState,
        `api/client/clientSessions/${sessionId}/bill/status`
    );
};

export const disconnectBillSocket = () => async (dispatch, getState, di) => {
    const { billWebSocket } = di[MODULE_NAME];

    if (billWebSocket.socket.connection) {
        billWebSocket.close();
    }
};

export const setAddonAsSelected = (virtualId, quantity) => async (dispatch) => {
    dispatch({
        type: ACTION_TYPES.SET_ADDON_AS_SELECTED,
        payload: { virtualId, quantity, isSelected: true }
    });
};

export const setAddonAsUnselected = (virtualId, quantity) => async (dispatch) => {
    dispatch({
        type: ACTION_TYPES.SET_ADDON_AS_SELECTED,
        payload: { virtualId, quantity, isSelected: false }
    });
};

export const addAddon = (virtualId) => async (dispatch) => {
    dispatch({
        type: ACTION_TYPES.ADD_ADDON,
        payload: virtualId
    });
};

export const removeAddon = (virtualId, position) => async (dispatch) => {
    dispatch({
        type: ACTION_TYPES.REMOVE_ADDON,
        payload: { virtualId, position }
    });
};

export const submitCloseBill = (context, paymentMethod) => async (dispatch, getState) => {
    const { sessionId } = context;
    const fingerprint = selectFingerprint(getState());

    dispatch({
        type: ACTION_TYPES.SUBMIT_ASK_FOR_BILL
    });

    await fetch(`${getApiUrl()}/api/client/clientSessions/${sessionId}/bill/status`, {
        method: 'PATCH',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            isClosed: true,
            paymentMethod,
            fingerprint
        })
    }).then((response) => {
        if (response.ok) {
            dispatch({
                type: ACTION_TYPES.ASKED_FOR_BILL
            });
        } else {
            return response.json().then((json) => { throw new ApiError(json); });
        }
    }).catch((error) => {
        dispatch({
            type: ACTION_TYPES.SUBMIT_ASK_FOR_BILL_FAILED,
            payload: error?.firstMessage
        });
    });
};

export const changeComment = (comment) => (dispatch) => {
    dispatch({
        type: ACTION_TYPES.COMMENT_CHANGED,
        payload: comment
    });
};

export const cancelOrder = (context) => async (dispatch, getState) => {
    const { sessionId } = context;
    const clientFingerprint = selectFingerprint(getState());

    await fetch(`${getApiUrl()}/api/client/clientSessions/${sessionId}/bill/payments`, {
        method: 'PATCH',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            clientFingerprint
        })
    }).then((response) => {
        if (response.ok) {
            dispatch({
                type: ACTION_TYPES.ORDER_CANCELLED
            });
        } else {
            return response.json().then((json) => { throw new ApiError(json); });
        }
    }).catch(() => {
        dispatch({
            type: ACTION_TYPES.CANCEL_ORDER_FAILED
        });
    });
};

export const repayment = (context, paymentContinueUrl) => async (dispatch, getState) => {
    const { sessionId } = context;
    const clientFingerprint = selectFingerprint(getState());

    await fetch(`${getApiUrl()}/api/client/clientSessions/${sessionId}/bill/payments`, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            clientFingerprint,
            paymentContinueUrl
        })
    }).then((response) => {
        if (response.ok) {
            return response.json();
        } else {
            return response.json().then((json) => { throw new ApiError(json); });
        }
    }).then((data) => {
        dispatch({
            type: ACTION_TYPES.REPAYMENT_SUCCESS,
            payload: data
        });
    }).catch(() => {
        dispatch({
            type: ACTION_TYPES.REPAYMENT_FAILED
        });
    });
};

export const clearCancelOrderErrorMessage = () => (dispatch) => {
    dispatch({
        type: ACTION_TYPES.CLEAR_CANCEL_ORDER_ERROR
    });
};

export const clearRepaymentErrorMessage = () => (dispatch) => {
    dispatch({
        type: ACTION_TYPES.CLEAR_REPAYMENT_ERROR
    });
};

export const clearAskForBillError = () => (dispatch) => {
    dispatch({
        type: ACTION_TYPES.CLEAR_ASK_FOR_BILL_ERROR
    });
};
